export const BIZY_EYE_PROXIMITY_PX = 80;

export const BIZY_VIEWBOX = {
	width: 316,
	height: 278,
} as const;

/** Approximate midpoint between left and right eyes in viewBox coordinates. */
export const BIZY_EYE_CENTER = {
	x: 139,
	y: 104,
} as const;

const MAX_EYE_OFFSET_X = 3.5;
const MAX_EYE_OFFSET_Y_DOWN = 3.5;
const MAX_EYE_OFFSET_Y_UP = 5.5;
const FULL_LOOK_DISTANCE = 40;

export type EyeOffset = {
	x: number;
	y: number;
};

export const distanceToRect = (pointerX: number, pointerY: number, rect: DOMRect): number => {
	const dx = Math.max(rect.left - pointerX, 0, pointerX - rect.right);
	const dy = Math.max(rect.top - pointerY, 0, pointerY - rect.bottom);
	return Math.hypot(dx, dy);
};

const rotateVector = (x: number, y: number, degrees: number): { x: number; y: number } => {
	if (degrees === 0) {
		return { x, y };
	}

	const radians = (degrees * Math.PI) / 180;
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);

	return {
		x: x * cos - y * sin,
		y: x * sin + y * cos,
	};
};

const MATRIX_ROTATE_PATTERN = /^matrix\(\s*([^,]+),\s*([^,]+)/;

export const getElementRotationDegrees = (element: Element): number => {
	const { transform } = getComputedStyle(element);
	const match = transform?.match(MATRIX_ROTATE_PATTERN);
	if (!match) {
		return 0;
	}

	const a = Number.parseFloat(match[1]);
	const b = Number.parseFloat(match[2]);
	if (!Number.isFinite(a) || !Number.isFinite(b)) {
		return 0;
	}

	return (Math.atan2(b, a) * 180) / Math.PI;
};

export const computeEyeOffset = (pointerX: number, pointerY: number, rect: DOMRect, bodyRotation = 0, proximityPx = BIZY_EYE_PROXIMITY_PX): EyeOffset => {
	if (rect.width <= 0 || rect.height <= 0) {
		return { x: 0, y: 0 };
	}

	const distance = distanceToRect(pointerX, pointerY, rect);
	if (distance > proximityPx) {
		return { x: 0, y: 0 };
	}

	const proximity = 1 - distance / proximityPx;
	const viewX = ((pointerX - rect.left) / rect.width) * BIZY_VIEWBOX.width;
	const viewY = ((pointerY - rect.top) / rect.height) * BIZY_VIEWBOX.height;

	const { x: dx, y: dy } = rotateVector(viewX - BIZY_EYE_CENTER.x, viewY - BIZY_EYE_CENTER.y, -bodyRotation);
	const magnitude = Math.hypot(dx, dy);
	if (magnitude === 0) {
		return { x: 0, y: 0 };
	}

	const lookStrength = Math.min(magnitude / FULL_LOOK_DISTANCE, 1);
	const offsetStrength = proximity * lookStrength;
	const maxY = dy < 0 ? MAX_EYE_OFFSET_Y_UP : MAX_EYE_OFFSET_Y_DOWN;

	return {
		x: (dx / magnitude) * MAX_EYE_OFFSET_X * offsetStrength,
		y: (dy / magnitude) * maxY * offsetStrength,
	};
};

const BIZY_BODY_SELECTOR = '[data-bizy-body]';

type EyeTrackingSubscriber = {
	element: Element;
	onUpdate: (offset: EyeOffset) => void;
};

const getBodyRotationDegrees = (svg: Element): number => {
	const body = svg.querySelector(BIZY_BODY_SELECTOR);
	return body ? getElementRotationDegrees(body) : 0;
};

class BizyEyeTrackingManager {
	private subscribers = new Set<EyeTrackingSubscriber>();
	private pointer = { x: Number.NaN, y: Number.NaN };
	private rafId: number | null = null;
	private listening = false;

	register(subscriber: EyeTrackingSubscriber) {
		this.subscribers.add(subscriber);
		this.ensureListening();
		this.ensureTicking();
	}

	unregister(subscriber: EyeTrackingSubscriber) {
		this.subscribers.delete(subscriber);
		subscriber.onUpdate({ x: 0, y: 0 });

		if (this.subscribers.size === 0) {
			this.stopListening();
		}
	}

	private ensureListening() {
		if (this.listening) {
			return;
		}

		window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
		document.addEventListener('pointerout', this.handlePointerOut, { passive: true });
		this.listening = true;
	}

	private stopListening() {
		if (!this.listening) {
			return;
		}

		window.removeEventListener('pointermove', this.handlePointerMove);
		document.removeEventListener('pointerout', this.handlePointerOut);
		this.stopTicking();
		this.listening = false;
		this.pointer = { x: Number.NaN, y: Number.NaN };
	}

	private ensureTicking() {
		if (this.rafId !== null) {
			return;
		}

		this.rafId = requestAnimationFrame(this.tick);
	}

	private stopTicking() {
		if (this.rafId === null) {
			return;
		}

		cancelAnimationFrame(this.rafId);
		this.rafId = null;
	}

	private handlePointerMove = (event: PointerEvent) => {
		this.pointer = { x: event.clientX, y: event.clientY };
	};

	private handlePointerOut = (event: PointerEvent) => {
		if (event.relatedTarget === null) {
			this.clearPointer();
		}
	};

	private clearPointer() {
		if (Number.isNaN(this.pointer.x) && Number.isNaN(this.pointer.y)) {
			return;
		}

		this.pointer = { x: Number.NaN, y: Number.NaN };

		for (const subscriber of this.subscribers) {
			subscriber.onUpdate({ x: 0, y: 0 });
		}
	}

	private tick = () => {
		this.rafId = null;
		this.updateAll();

		if (this.subscribers.size > 0) {
			this.ensureTicking();
		}
	};

	private updateAll = () => {
		if (Number.isNaN(this.pointer.x) || Number.isNaN(this.pointer.y)) {
			return;
		}

		for (const subscriber of this.subscribers) {
			const rect = subscriber.element.getBoundingClientRect();
			const bodyRotation = getBodyRotationDegrees(subscriber.element);
			const offset = computeEyeOffset(this.pointer.x, this.pointer.y, rect, bodyRotation);
			subscriber.onUpdate(offset);
		}
	};
}

export const bizyEyeTrackingManager = new BizyEyeTrackingManager();
